import { Component } from '@angular/core';
import { BreadcrumbsComponent } from 'components/breadcrumbs/breadcrumbs.component';
import { TableComponent } from 'components/table/table.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Breadcrumb } from 'types/breadcrumb.interface';
import { API_AUDIT_LIST, ApiService } from 'services/api.service';
import { TableColumn, FilterType } from 'types/table-column.interface';
import { ToastService } from 'services/toast.service';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-audit-list-page',
  standalone: true,
  imports: [TableComponent, TranslateModule, BreadcrumbsComponent, NgClass],
  templateUrl: './audit-list-page.component.html',
  styleUrl: './audit-list-page.component.scss'
})
export class AuditListPageComponent {
  public readonly auditEndpoint = API_AUDIT_LIST;
  public isExcelDownloading = false;

  constructor(
    private apiService: ApiService,
    private toastService: ToastService,
    private translate: TranslateService
  ) {}

  breadcrumbs: Breadcrumb[] = [
    {
      title: 'components.breadcrumbs.admin',
      url: '/'
    },
    {
      title: 'components.breadcrumbs.audit',
      url: '/audits'
    }
  ];

  columns: TableColumn[] = [
    {
      name: 'pages.audit.dateTime',
      key: 'createdOn',
      type: 'dateTime',
      sortEnabled: true,
      filter: FilterType.DateTime
    },
    {
      name: 'pages.audit.createdBy',
      key: 'createdBy',
      sortEnabled: true,
      filter: FilterType.Search,
      sortKey: 'createdBy',
      customLogic: 'jsonUserName'
    },
    {
      name: 'pages.audit.ipAddress',
      key: 'createdBy',
      sortEnabled: true,
      filter: FilterType.Search,
      sortKey: 'ipAddress',
      customLogic: 'jsonIpAddress'
    },
    {
      name: 'pages.audit.type',
      key: 'type',
      sortEnabled: true,
      multiselectFilterName: 'types',
      filter: FilterType.MultiSelect,
      filterOptions: ['Update', 'Create', 'Delete']
    },
    {
      name: 'pages.audit.table',
      key: 'table',
      sortEnabled: true,
      filter: FilterType.Search
    },
    {
      name: 'pages.audit.result',
      key: 'changes',
      sortEnabled: true,
      customLogic: 'auditResult'
    },
    {
      name: 'pages.audit.system',
      key: 'createdBy',
      sortEnabled: true,
      filter: FilterType.Search,
      sortKey: 'system',
      customLogic: 'jsonSystem'
    }
  ];

  downloadExcel(): void {
    this.isExcelDownloading = true;
    this.apiService.downloadAuditExcel().subscribe({
      next: (response: HttpResponse<Blob>) => {
        this.isExcelDownloading = false;
        const blob = new Blob([response.body!], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'audita_ierakstu_zurnals.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.notifyIfExportCapped(response.headers);
      },
      error: (error: any) => {
        this.isExcelDownloading = false;
        console.error(error);
        this.toastService.showRequestError();
      }
    });
  }

  // LNA-2053. The backend caps the export (20 000 rows) and reports how many rows matched, so the
  // operator is told when the file holds only the top of the list.
  private notifyIfExportCapped(headers: HttpHeaders): void {
    const total = Number(headers.get('X-Total-Count'));
    const exported = Number(headers.get('X-Exported-Count'));

    if (!total || !exported || exported >= total) {
      return;
    }

    this.toastService.showInfo(
      this.translate.instant('pages.audit.exportCapped', {
        exported: exported.toLocaleString('lv-LV'),
        total: total.toLocaleString('lv-LV')
      }),
      '',
      { delay: 10000 }
    );
  }
}
